import { alpha } from '@mui/material/styles';
import { colors, typography } from '../../styles/theme';
import {
  APP_MODAL_DIVIDER,
  APP_MODAL_TEXT,
  appModalFieldSx,
  appModalPrimaryButtonSx,
  appModalSecondaryButtonSx,
} from '../../Components/appModalChrome.styles';
import { LEAD_STAGE_COLORS, LEAD_TEMPERATURE_COLORS } from '../leadsConfig';

export const LEADS_PANEL_BG = '#1c1c1c';
export const LEADS_PANEL_BORDER = '#4a4a4a';
export const LEADS_BG_ELEVATED = '#242424';
export const LEADS_TEXT_PRIMARY = '#D4D4D4';
export const LEADS_TEXT_SECONDARY = '#ABABAB';
export const LEADS_TEXT_MUTED = 'rgba(255,255,255,0.45)';
export const LEADS_DANGER = '#EE5757';
/** Fondo del chip «N consultas nuevas» (card real y su skeleton). */
export const LEADS_UNSEEN_BADGE_BG = 'rgba(72,146,145,0.22)';
/** Paleta de skeleton (la misma de Registros / Listas). */
export const LEADS_SKELETON_PRIMARY = '#383838';
export const LEADS_SKELETON_MUTED = '#2E2E2E';

export const leadsPanelPaperSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  height: '100%',
  minHeight: '100%',
  bgcolor: LEADS_PANEL_BG,
  backgroundImage: 'none',
  color: colors.textPrimary,
  boxShadow: '0 18px 45px rgba(15,23,42,0.55)',
  border: `1px solid ${LEADS_PANEL_BORDER}`,
  borderRadius: { xs: 0, sm: '16px' },
};

export const leadsToolbarZoneSx = {
  flexShrink: 0,
  bgcolor: LEADS_PANEL_BG,
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  px: 2.5,
  pt: 2,
  pb: 1.5,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.25,
};

/** Misma barra que el toolbar de Registros mientras llegan contadores. */
export const leadsToolbarSkeletonSx = { bgcolor: LEADS_SKELETON_PRIMARY, borderRadius: '4px' };

export const leadsChipsRowSx = {
  display: 'flex',
  gap: 0.75,
  overflowX: 'auto',
  pb: 0.25,
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
};

/** Chip de etapa del filtro (con contador). */
/** Acento teal de lo elegido (chip de filtro activo, lead elegido). */
export const LEADS_SELECTED_ACCENT = '#6da8a7';

export const getLeadStageFilterChipSx = (active, stage) => {
  const accent = LEAD_STAGE_COLORS[stage] || LEADS_SELECTED_ACCENT;
  return {
    flexShrink: 0,
    textTransform: 'none',
    height: 30,
    minHeight: 30,
    px: '10px',
    borderRadius: '6px',
    fontFamily: typography.fontFamily.primary,
    fontSize: '12px',
    fontWeight: active ? 600 : 500,
    lineHeight: 1,
    gap: '6px',
    border: '1px solid',
    borderColor: active ? accent : 'rgba(255,255,255,0.14)',
    bgcolor: active ? `${accent}26` : 'transparent',
    color: active ? '#FFFFFF' : LEADS_TEXT_SECONDARY,
    boxShadow: 'none',
    '&:hover': { bgcolor: `${accent}1A`, borderColor: accent, boxShadow: 'none' },
  };
};

export const leadsCountBadgeSx = {
  minWidth: 18,
  height: 18,
  px: '5px',
  borderRadius: '9px',
  bgcolor: 'rgba(255,255,255,0.10)',
  color: '#FFFFFF',
  fontSize: '11px',
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
};

/** Pill de etapa (tarjeta y detalle). */
export const getLeadStagePillSx = (stage) => {
  const accent = LEAD_STAGE_COLORS[stage] || '#6da8a7';
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: 22,
    px: '8px',
    borderRadius: '11px',
    bgcolor: `${accent}22`,
    color: accent,
    fontSize: '11px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    '&::before': {
      content: '""',
      width: 6,
      height: 6,
      borderRadius: '50%',
      bgcolor: accent,
    },
  };
};

/** Chip de temperatura del filtro (tablero V3). */
export const getLeadTemperatureFilterChipSx = (active, temperature) => {
  const accent = LEAD_TEMPERATURE_COLORS[temperature] || '#6da8a7';
  return {
    flexShrink: 0,
    textTransform: 'none',
    height: 30,
    minHeight: 30,
    px: '10px',
    borderRadius: '6px',
    fontFamily: typography.fontFamily.primary,
    fontSize: '12px',
    fontWeight: active ? 600 : 500,
    lineHeight: 1,
    gap: '6px',
    border: '1px solid',
    borderColor: active ? accent : 'rgba(255,255,255,0.14)',
    bgcolor: active ? `${accent}26` : 'transparent',
    color: active ? '#FFFFFF' : LEADS_TEXT_SECONDARY,
    boxShadow: 'none',
    '&:hover': { bgcolor: `${accent}1A`, borderColor: accent, boxShadow: 'none' },
  };
};

/** Pill de temperatura (detalle). */
export const getLeadTemperaturePillSx = (temperature) => {
  const accent = LEAD_TEMPERATURE_COLORS[temperature] || '#6da8a7';
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: 22,
    px: '8px',
    borderRadius: '11px',
    bgcolor: `${accent}22`,
    color: accent,
    fontSize: '11px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    '&::before': {
      content: '""',
      width: 6,
      height: 6,
      borderRadius: '50%',
      bgcolor: accent,
    },
  };
};

/** Punto de temperatura en la tarjeta del índice (tooltip con el nivel). */
export const getLeadTemperatureDotSx = (temperature) => {
  const accent = LEAD_TEMPERATURE_COLORS[temperature] || '#6da8a7';
  return {
    width: 8,
    height: 8,
    borderRadius: '50%',
    bgcolor: accent,
    flexShrink: 0,
    display: 'inline-block',
  };
};

/** Tarjeta del índice: acento por temperatura (V3). */
export const getLeadCardSx = (temperature) => ({
  position: 'relative',
  borderRadius: '8px',
  bgcolor: LEADS_BG_ELEVATED,
  borderLeft: `3px solid ${LEAD_TEMPERATURE_COLORS[temperature] || '#6da8a7'}`,
  px: '12px',
  py: '10px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
  '&:hover': { bgcolor: '#2a2a2a' },
  '&:focus-visible': { outline: `1px solid ${colors.primary}` },
});

export const leadCardTitleSx = {
  color: '#F3F4F6',
  fontFamily: typography.fontFamily.heading,
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '18px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
};

export const leadCardMetaSx = {
  color: LEADS_TEXT_SECONDARY,
  fontFamily: typography.fontFamily.primary,
  fontSize: '12px',
  lineHeight: '16px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
};

/** Resumen del índice (izquierda de “Ordenar por”): texto chico apagado, recorta con elipsis. */
export const leadsSummaryRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  flex: '1 1 auto',
  minWidth: 0,
};

export const leadsSummaryTextSx = {
  color: LEADS_TEXT_MUTED,
  fontFamily: typography.fontFamily.primary,
  fontSize: '12px',
  lineHeight: '16px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
};

/** “Limpiar”: link de texto primario Terrio, mismo tamaño que el resumen. */
export const leadsSummaryClearButtonSx = {
  textTransform: 'none',
  minWidth: 0,
  minHeight: 24,
  px: 0.5,
  flexShrink: 0,
  color: colors.primary,
  fontFamily: typography.fontFamily.primary,
  fontSize: '12px',
  fontWeight: 500,
  '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', color: colors.primaryLight },
};

/**
 * Espaciado único de los formularios de Leads (Nueva consulta, Nuevo lead,
 * Editar datos). Sin helper reservado (' '): un error aparece debajo de su
 * campo solo cuando existe.
 */
/** 20 px entre secciones (contacto, ¿qué busca? / ¿qué consultó?, canal…). */
export const LEADS_FORM_SECTION_GAP = 2.5;
/** 12 px entre campos de una misma sección (Nombre → Teléfono / Email). */
export const LEADS_FORM_FIELD_GAP = 1.5;
/** 6 px del título de sección («Lead», «Canal») a su control. */
export const LEADS_FORM_LABEL_GAP = 0.75;

/** Rótulo de grupo de día («Hoy», «Ayer»…): timeline de Leads e Historial. */
export const activityGroupLabelSx = {
  fontSize: '11px',
  fontWeight: 600,
  color: LEADS_TEXT_MUTED,
  mb: 0.75,
};

export const leadsSectionTitleSx = {
  color: LEADS_TEXT_MUTED,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

/** Primario de diálogos Terrio (`appModalPrimaryButtonSx`). */
export const leadsPrimaryButtonSx = {
  ...appModalPrimaryButtonSx,
};

/** Secundario outlined de diálogos Terrio (`appModalSecondaryButtonSx`). */
export const leadsGhostButtonSx = {
  ...appModalSecondaryButtonSx,
  border: `1px solid ${APP_MODAL_DIVIDER}`,
};

export const leadsDialogPaperSx = {
  width: 'min(520px, 95vw)',
  maxWidth: '520px',
  bgcolor: LEADS_PANEL_BG,
  backgroundImage: 'none',
  color: '#F9FAFB',
  border: `1px solid ${LEADS_PANEL_BORDER}`,
  borderRadius: '14px',
};

export const leadsDialogTitleSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  px: 2.5,
  py: 1.75,
  color: '#F9FAFB',
  fontFamily: typography.fontFamily.heading,
  fontSize: '17px',
  fontWeight: 600,
};

/**
 * TextField de diálogos Terrio (`appModalFieldSx`).
 * Sin `height` fijo: el multiline de “qué busca” no se recorta.
 */
export const leadsTextFieldSx = {
  ...appModalFieldSx,
  '& .MuiInputLabel-root': {
    color: 'rgba(251,251,251,0.72)',
    fontFamily: typography.fontFamily.primary,
    fontSize: '14px',
  },
  '& .MuiInputLabel-root.Mui-focused': { color: colors.primary },
  '& .MuiInputLabel-root.Mui-error': { color: LEADS_DANGER },
  '& .MuiOutlinedInput-root': {
    ...appModalFieldSx['& .MuiOutlinedInput-root'],
    height: 'auto',
    minHeight: 40,
    bgcolor: colors.backgroundSurface,
  },
  '& .MuiOutlinedInput-root.Mui-error fieldset': { borderColor: LEADS_DANGER },
  '& .MuiOutlinedInput-root.Mui-error:hover fieldset': { borderColor: LEADS_DANGER },
  '& .MuiOutlinedInput-input': {
    ...appModalFieldSx['& .MuiOutlinedInput-input'],
    color: APP_MODAL_TEXT,
    WebkitTextFillColor: APP_MODAL_TEXT,
  },
  '& .MuiFormHelperText-root': {
    mx: 0,
    mt: '6px',
    fontSize: '12px',
    lineHeight: 1.45,
    color: 'rgba(251,251,251,0.45)',
    fontFamily: typography.fontFamily.primary,
  },
  '& .MuiFormHelperText-root.Mui-error': { color: LEADS_DANGER },
};

export const leadsSelectMenuProps = {
  PaperProps: {
    sx: {
      bgcolor: LEADS_BG_ELEVATED,
      color: LEADS_TEXT_PRIMARY,
      backgroundImage: 'none',
      '& .MuiMenuItem-root': {
        fontSize: '14px',
        fontFamily: typography.fontFamily.primary,
        color: APP_MODAL_TEXT,
      },
      '& .MuiMenuItem-root:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
      '& .Mui-selected': { bgcolor: 'rgba(72,146,145,0.22) !important' },
    },
  },
  // Por encima del diálogo.
  sx: { zIndex: 2300 },
};

/** Error de un diálogo oscuro: mismo tono que `MetaWriteNote` (`colors.error`, fondo y borde suaves). */
export const leadsAlertSx = {
  borderRadius: '8px',
  px: 1.5,
  py: 1,
  fontSize: '13px',
  lineHeight: '18px',
  alignItems: 'flex-start',
  color: colors.error,
  bgcolor: alpha(colors.error, 0.08),
  border: `1px solid ${alpha(colors.error, 0.3)}`,
  '&.MuiAlert-outlinedError': {
    color: colors.error,
    backgroundColor: alpha(colors.error, 0.08),
    borderColor: alpha(colors.error, 0.3),
  },
  '& .MuiAlert-icon': {
    color: colors.error,
    fontSize: 18,
    mr: 1,
    p: 0,
    mt: '1px',
    opacity: 1,
  },
  '& .MuiAlert-message': { p: 0, overflow: 'visible' },
};

/**
 * Campo fecha / fecha y hora (filtros de Leads, consulta manual): ícono y popup
 * nativos en oscuro. `accent-color` teal: lo respeta Firefox en su selector; el
 * popup de Chrome no se puede estilar con CSS (queda su azul) y Safari usa el
 * color de acento del sistema.
 */
export const leadsDateFieldSx = {
  ...leadsTextFieldSx,
  '& input': { colorScheme: 'dark', accentColor: colors.primary },
};

/** Chip de etapa estirado a su celda (selectores en grilla: canal, modo de lead). */
export const leadsGridChipSx = { width: '100%', minWidth: 0, whiteSpace: 'nowrap' };

/** Chip chico de origen de una consulta («Manual», «WhatsApp»): ficha, actividad y tarjetas. */
export const leadInquiryTagSx = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 18,
  px: '6px',
  borderRadius: '9px',
  bgcolor: 'rgba(255,255,255,0.08)',
  color: LEADS_TEXT_SECONDARY,
  fontSize: '10.5px',
  fontWeight: 600,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  flexShrink: 0,
};
